import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { CHART_TYPE } from '@/_legacy/constants';
import { encodeQuestionQuery, QuestionQuery, unsetSort } from '@/services/api/resources/analytics/query';
import { col, cols } from '@/ui/components';
import NumberCard from '@/_legacy/views/Analytics/components/NumberCard.vue';
import { t, toUnixTimestamp } from '@/ui/i18n/i18n';
import { pushRoute } from '@/ui/helpers/uiHelper';
import GroupedChartCard from '@/_legacy/views/Analytics/components/GroupedChartCard.vue';
import { useTask } from '@/ui/concerns';
import { groupedQuestionList } from '@/services/api/resources/analytics';
import { QuestionGroupBy } from '@/services/api/resources/analytics/types';

interface ScoresChartsProps {
  query: QuestionQuery;
  scorePercentage: number;
}

export const ScoresCharts = defineComponent<ScoresChartsProps>({
  name: 'ScoresCharts',
  props: ['query', 'scorePercentage'],
  setup(props) {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const chartType = ref(CHART_TYPE.LINE);
    const chartGroupBy = ref(QuestionGroupBy.Week);
    const chartData = ref({});

    const getList = useTask(
      () => {
        initialLoading.value = true;
        return groupedQuestionList(chartGroupBy.value, unsetSort(props.query) as QuestionQuery);
      },
      response => {
        const dateFormatted = (start: Date | null) => start ? toUnixTimestamp(start.getTime()) : null;

        chartData.value = {
          labels: response.data.map(result => dateFormatted(result.group.start)),
          series: [response.data.map((result, index) => ({
            x: index,
            y: result.scorePercentage * 100,
            meta: {
              start: dateFormatted(result.group.start),
              end: dateFormatted(result.group.end),
            },
          }))],
        };

        initialLoading.value = false;
      },
    );

    watch([
      () => props.query,
      chartGroupBy,
    ], getList.run, { deep: true });

    const redirectToExplain = (query: QuestionQuery) => pushRoute({
      name: 'analyticsScoresExplain',
      query: encodeQuestionQuery(query),
    });

    return function render() {
      return h('div', [
        cols([
          col([
            h(NumberCard, {
              props: {
                loading: initialLoading.value,
                color: 'var(--analytics-green)',
                label: t('analytics.score-average'),
                percentage: props.scorePercentage,
              },
              class: ['h-full'],
              on: {
                numberClick: () => redirectToExplain(unsetSort(props.query) as QuestionQuery),
              },
            }),
          ], true, { 'number-column': true }),
          col([
            //legacy, not compatible with Vue typings
            //@ts-ignore
            h(GroupedChartCard, {
              class: ['w-full'],
              props: {
                color: 'green',
                loading: initialLoading.value,
                data: chartData.value,
                startsAt: toUnixTimestamp(props.query.start.getTime()),
                endsAt: toUnixTimestamp(props.query.end.getTime()),
                type: chartType.value,
                groupBy: chartGroupBy.value,
                tooltipText: `%${t('analytics.score-count')}`,
                high: 100,
                low: 0,
                formatYLabels: (value: string) => `${value}%`,
              },
              on: {
                typeChanged: (newType: CHART_TYPE) => {
                  chartType.value = newType;
                },
                groupByChanged: (newGroup: QuestionGroupBy) => {
                  chartGroupBy.value = newGroup;
                },
              },
            }),
          ]),
        ]),
      ]);
    };
  },
});
